/**
 * PowerPoint-style adjustment handles (the yellow `adj` handles).
 *
 * Faithful to the ECMA-376 preset geometry model (DrawingML `avLst`/`gdLst`/
 * `ahLst`): each adjustable shape has one or more adjustment VALUES that
 * parameterize its geometry, and one HANDLE per value placed where PowerPoint
 * puts it. Dragging a handle changes the value(s) and regenerates the path.
 *
 * Adjustment values here are normalized to [0..1] fractions (ECMA stores them
 * as 1/100000ths; the defaults below are the ECMA defaults divided by 100000).
 * Geometry is authored in normalized [0..1] space with Y UP (y = 1 is the TOP
 * edge), matching shape-paths.ts, then scaled onto the shape bounds.
 */
import type { ShapeBounds, ShapePath } from "./types.js";

export interface AdjustBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * A single adjustment handle. Positions are in normalized [0..1] shape space
 * (Y up). `position` returns where the handle sits given the current values;
 * `toAdjustments` maps a (clamped) dragged handle position back to a full,
 * updated value array. Both receive the bounds so aspect-dependent shapes
 * (e.g. a corner radius measured against min(w,h)) resolve correctly.
 */
export interface ShapeAdjustHandle {
  readonly position: (adj: readonly number[], bounds: AdjustBounds) => readonly [number, number];
  readonly toAdjustments: (
    nx: number,
    ny: number,
    adj: readonly number[],
    bounds: AdjustBounds,
  ) => number[];
}

export interface ShapeAdjustSpec {
  readonly defaults: readonly number[];
  readonly handles: readonly ShapeAdjustHandle[];
  /** Build the resolved path for the given bounds + adjustment values. */
  readonly build: (bounds: ShapeBounds, adj: readonly number[]) => ShapePath;
}

// ── path helpers (local, y-up normalized → bounds) ──────────────────────────
type NP = readonly [number, number];
const kappa = 0.5522847498307936;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function pt(b: ShapeBounds, p: NP) {
  return { x: b.x + p[0] * b.width, y: b.y + p[1] * b.height };
}
function polygon(b: ShapeBounds, pts: readonly NP[]): ShapePath {
  const [first, ...rest] = pts.map((p) => pt(b, p));
  if (!first) return [];
  return [
    { type: "move", x: first.x, y: first.y },
    ...rest.map((q) => ({ type: "line" as const, x: q.x, y: q.y })),
    { type: "close" },
  ];
}

// ── adjustable geometry builders ────────────────────────────────────────────

function roundedRectBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const ratio = clamp(adj[0] ?? 0.16667, 0, 0.5);
  const radius = Math.min(b.width, b.height) * ratio;
  const x0 = b.x;
  const x1 = b.x + b.width;
  const y0 = b.y;
  const y1 = b.y + b.height;
  const o = radius * kappa;
  return [
    { type: "move", x: x0 + radius, y: y0 },
    { type: "line", x: x1 - radius, y: y0 },
    { type: "cubic", x1: x1 - radius + o, y1: y0, x2: x1, y2: y0 + radius - o, x: x1, y: y0 + radius },
    { type: "line", x: x1, y: y1 - radius },
    { type: "cubic", x1: x1, y1: y1 - radius + o, x2: x1 - radius + o, y2: y1, x: x1 - radius, y: y1 },
    { type: "line", x: x0 + radius, y: y1 },
    { type: "cubic", x1: x0 + radius - o, y1: y1, x2: x0, y2: y1 - radius + o, x: x0, y: y1 - radius },
    { type: "line", x: x0, y: y0 + radius },
    { type: "cubic", x1: x0, y1: y0 + radius - o, x2: x0 + radius - o, y2: y0, x: x0 + radius, y: y0 },
    { type: "close" },
  ];
}

/** Corner-radius handle on the top edge, offset from a corner by the radius. */
function radiusHandle(corner: "TL" | "TR"): ShapeAdjustHandle {
  return {
    position: (adj, b) => {
      const radius = Math.min(b.width, b.height) * clamp(adj[0] ?? 0.16667, 0, 0.5);
      const fx = radius / b.width;
      return corner === "TL" ? [fx, 1] : [1 - fx, 1];
    },
    toAdjustments: (nx, _ny, _adj, b) => {
      const radiusPx = (corner === "TL" ? nx : 1 - nx) * b.width;
      return [clamp(radiusPx / Math.min(b.width, b.height), 0, 0.5)];
    },
  };
}

// Right/left/up/down block arrows. adj0 = shaft thickness fraction (of the
// cross dimension); adj1 = arrowhead length fraction (of the along dimension).
function rightArrowBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const a1 = clamp(adj[0] ?? 0.5, 0.05, 1);
  const a2 = clamp(adj[1] ?? 0.4, 0.05, 0.95);
  const top = 0.5 + a1 / 2;
  const bot = 0.5 - a1 / 2;
  const base = 1 - a2;
  return polygon(b, [
    [0, bot],
    [base, bot],
    [base, 0],
    [1, 0.5],
    [base, 1],
    [base, top],
    [0, top],
  ]);
}
function leftArrowBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const a1 = clamp(adj[0] ?? 0.5, 0.05, 1);
  const a2 = clamp(adj[1] ?? 0.4, 0.05, 0.95);
  const top = 0.5 + a1 / 2;
  const bot = 0.5 - a1 / 2;
  return polygon(b, [
    [1, bot],
    [a2, bot],
    [a2, 0],
    [0, 0.5],
    [a2, 1],
    [a2, top],
    [1, top],
  ]);
}
function upArrowBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const a1 = clamp(adj[0] ?? 0.5, 0.05, 1);
  const a2 = clamp(adj[1] ?? 0.4, 0.05, 0.95);
  const right = 0.5 + a1 / 2;
  const left = 0.5 - a1 / 2;
  const base = 1 - a2;
  return polygon(b, [
    [left, 0],
    [right, 0],
    [right, base],
    [1, base],
    [0.5, 1],
    [0, base],
    [left, base],
  ]);
}
function downArrowBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const a1 = clamp(adj[0] ?? 0.5, 0.05, 1);
  const a2 = clamp(adj[1] ?? 0.4, 0.05, 0.95);
  const right = 0.5 + a1 / 2;
  const left = 0.5 - a1 / 2;
  return polygon(b, [
    [left, 1],
    [right, 1],
    [right, a2],
    [1, a2],
    [0.5, 0],
    [0, a2],
    [left, a2],
  ]);
}

// Shaft-thickness handle (cross axis) + head-length handle (along axis).
const horizArrowHandles = (headSide: "right" | "left"): ShapeAdjustHandle[] => [
  {
    // shaft thickness: vertical drag on the tail edge
    position: (adj) => [headSide === "right" ? 0 : 1, 0.5 + clamp(adj[0] ?? 0.5, 0.05, 1) / 2],
    toAdjustments: (_nx, ny, adj) => [clamp((ny - 0.5) * 2, 0.05, 1), adj[1] ?? 0.4],
  },
  {
    // head length: horizontal drag at the head base
    position: (adj) => {
      const a1 = clamp(adj[0] ?? 0.5, 0.05, 1);
      const a2 = clamp(adj[1] ?? 0.4, 0.05, 0.95);
      return [headSide === "right" ? 1 - a2 : a2, 0.5 + a1 / 2];
    },
    toAdjustments: (nx, _ny, adj) => [adj[0] ?? 0.5, clamp(headSide === "right" ? 1 - nx : nx, 0.05, 0.95)],
  },
];
const vertArrowHandles = (headSide: "up" | "down"): ShapeAdjustHandle[] => [
  {
    position: (adj) => [0.5 + clamp(adj[0] ?? 0.5, 0.05, 1) / 2, headSide === "up" ? 0 : 1],
    toAdjustments: (nx, _ny, adj) => [clamp((nx - 0.5) * 2, 0.05, 1), adj[1] ?? 0.4],
  },
  {
    position: (adj) => {
      const a1 = clamp(adj[0] ?? 0.5, 0.05, 1);
      const a2 = clamp(adj[1] ?? 0.4, 0.05, 0.95);
      return [0.5 + a1 / 2, headSide === "up" ? 1 - a2 : a2];
    },
    toAdjustments: (_nx, ny, adj) => [adj[0] ?? 0.5, clamp(headSide === "up" ? 1 - ny : ny, 0.05, 0.95)],
  },
];

function chevronBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const d = clamp(adj[0] ?? 0.45, 0.05, 0.5);
  return polygon(b, [
    [0, 0],
    [1 - d, 0],
    [1, 0.5],
    [1 - d, 1],
    [0, 1],
    [d, 0.5],
  ]);
}
function homePlateBuild(b: ShapeBounds, adj: readonly number[]): ShapePath {
  const d = clamp(adj[0] ?? 0.25, 0.05, 0.9);
  return polygon(b, [
    [0, 0],
    [1 - d, 0],
    [1, 0.5],
    [1 - d, 1],
    [0, 1],
  ]);
}
const pointDepthHandle: ShapeAdjustHandle = {
  position: (adj) => [1 - clamp(adj[0] ?? 0.45, 0.05, 0.9), 0],
  toAdjustments: (nx) => [clamp(1 - nx, 0.05, 0.9)],
};

function starBuild(points: number, b: ShapeBounds, adj: readonly number[]): ShapePath {
  const inner = clamp(adj[0] ?? starDefaultInner(points), 0.05, 0.95);
  const verts: NP[] = Array.from({ length: points * 2 }, (_, i) => {
    const angle = Math.PI / 2 + (i * Math.PI) / points;
    const r = i % 2 === 0 ? 0.5 : 0.5 * inner;
    return [0.5 + Math.cos(angle) * r, 0.5 + Math.sin(angle) * r];
  });
  return polygon(b, verts);
}
function starDefaultInner(points: number): number {
  const table: Record<number, number> = {
    4: 0.38, 5: 0.45, 6: 0.5, 7: 0.55, 8: 0.58, 10: 0.62, 12: 0.66, 16: 0.72, 24: 0.8, 32: 0.85,
  };
  return table[points] ?? 0.5;
}
function starHandle(points: number): ShapeAdjustHandle {
  // First inner vertex (index 1); drag radially to set the inner radius ratio.
  const angle = Math.PI / 2 + Math.PI / points;
  return {
    position: (adj) => {
      const r = 0.5 * clamp(adj[0] ?? starDefaultInner(points), 0.05, 0.95);
      return [0.5 + Math.cos(angle) * r, 0.5 + Math.sin(angle) * r];
    },
    toAdjustments: (nx, ny) => {
      const dist = Math.hypot(nx - 0.5, ny - 0.5); // normalized; treated isotropically
      return [clamp((dist / 0.5), 0.05, 0.95)];
    },
  };
}

// ── registry ────────────────────────────────────────────────────────────────

const SPECS: Record<string, ShapeAdjustSpec> = {
  RoundRectangle: { defaults: [0.16667], handles: [radiusHandle("TL")], build: roundedRectBuild },
  RightArrow: { defaults: [0.5, 0.4], handles: horizArrowHandles("right"), build: rightArrowBuild },
  LeftArrow: { defaults: [0.5, 0.4], handles: horizArrowHandles("left"), build: leftArrowBuild },
  UpArrow: { defaults: [0.5, 0.4], handles: vertArrowHandles("up"), build: upArrowBuild },
  DownArrow: { defaults: [0.5, 0.4], handles: vertArrowHandles("down"), build: downArrowBuild },
  Chevron: { defaults: [0.45], handles: [pointDepthHandle], build: chevronBuild },
  HomePlate: { defaults: [0.25], handles: [pointDepthHandle], build: homePlateBuild },
};
for (const n of [4, 5, 6, 7, 8, 10, 12, 16, 24, 32]) {
  SPECS[`Star${n}`] = {
    defaults: [starDefaultInner(n)],
    handles: [starHandle(n)],
    build: (b, adj) => starBuild(n, b, adj),
  };
}

/** Adjustment spec for a shape, or undefined if it has no adjustment handles. */
export function shapeAdjustSpec(shapeType: string): ShapeAdjustSpec | undefined {
  return SPECS[shapeType];
}

/** Default adjustment values for a shape (empty array if none). */
export function shapeAdjustDefaults(shapeType: string): readonly number[] {
  return SPECS[shapeType]?.defaults ?? [];
}

/**
 * Build the path for an adjustable shape. Returns undefined for shapes without
 * adjustment support so callers fall through to fixed geometry.
 */
export function createAdjustableShapePath(
  shapeType: string,
  bounds: ShapeBounds,
  adjustments?: readonly number[],
): ShapePath | undefined {
  const spec = SPECS[shapeType];
  if (!spec) return undefined;
  return spec.build(bounds, adjustments && adjustments.length > 0 ? adjustments : spec.defaults);
}
